import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'

import ApiClient from '@src/lib/api_client'
import { Button } from '@src/elements/button/button'
import { Dialog } from '@src/components/dialog/dialog'
import { Form } from '@src/components/form/form'
import { Input } from '@src/elements/input/input'
import { Icon } from '@src/elements/icon/icon'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Spacer } from '@src/elements/spacer/spacer'
import { Body1, Subtitle1 } from '@src/elements/typography/typography'
import { API } from '@src/lib/api_path'
import { LanguageSwitch } from '@src/components/language-switch/language-switch'
import { InputFootnote } from '@src/elements/input/input-footnote'
import { highlightFulfilledRequirements } from '@src/util/functions'
import { validatePassword } from '@src/util/validation'
import { PasswordRequirements } from '@src/components/password-requirements/password-requirements'
import { MemberService } from '@src/service/member'
import SessionManager from '@src/lib/session_manager'

import './reset-password.sass'

@Component
export class RequestResetPasswordRoute extends Vue {

  email: string = ''

  error: boolean = false
  success: boolean = false
  requestInProgress: boolean = false
  client = new ApiClient(API.TRAINERS_API.PASSWORDS, { skipAuth: true })

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('RESET_PASSWORD')}`
  }

  requestReset () {
    this.requestInProgress = true
    this.error = false
    return this.client.post('request_reset', { email: this.email })
      .then(() => this.success = true)
      .catch(() => this.error = true)
      .finally(() => this.requestInProgress = false)
  }

  render (h: CreateElement) {
    return <div class='reset-password-page'>

      <Dialog>
        {this.success ?
          <div>
            <Icon name='brand/envelope' class='success-icon' />
            <Spacer />
            <Body1 align='center'>{this.$t('PAGE.RESET_PASSWORD.SUCCESS.REQUEST')}</Body1>
            <Spacer />
            <div class='btns-row'>
              <Button class='cancel-button' label={this.$t('PAGE.RESET_PASSWORD.BACK_TO_LOGIN')} click={() => this.$router.push('/')} type='elevated' ref='login-link' />
            </div>
          </div>

          : <div>
            <Subtitle1 align='center' class='title'>{this.$t('PAGE.RESET_PASSWORD.TITLE.REQUEST')}</Subtitle1>
            <Spacer />
            <Body1 align='center'>{this.$t('PAGE.RESET_PASSWORD.INSTRUCTIONS.REQUEST')}</Body1>
            <Spacer />
            <Form>
              <Input ref='input-email' label={this.$t('EMAIL')} autofocus={true} valueChanged={(value: string) => this.email = value} value={this.email}
                     class={this.error ? 'error' : ''} />

              {this.error ? <InputFootnote class='error'>{this.$t('PAGE.RESET_PASSWORD.ERROR.REQUEST')}</InputFootnote> : null}

              {this.requestInProgress ?
                <Loader color='white' />

                : <div class='btns-row'>
                  <Button class='cancel-button' label={this.$t('CANCEL')} click={() => this.$router.push('/')} type='outline' ref='login-link' />
                  <Button class='submit-button' label={this.$t('RESET')} disabled={!(this.email)} click={this.requestReset} />
                </div>}
            </Form>
          </div>}

        <LanguageSwitch />
      </Dialog>
    </div>
  }
}

@Component
export class ResetPasswordRoute extends Vue {

  error: boolean = false
  success: boolean = false

  newPassError: boolean = false
  confirmError: boolean = false

  password: string = ''
  confirmPassword: string = ''

  token: string = ''
  requestInProgress: boolean = false

  highlightRequirements = {
    minLength: false,
    oneLetter: false,
    oneNumber: false,
    sameAsConfirm: false
  }

  performReset () {
    this.validate()

    if (!this.error && !this.confirmError && !this.newPassError) {

      this.requestInProgress = true

      return new MemberService().resetPassword(this.token, this.password)
        .then(() => this.success = true)
        .catch(() => this.error = true)
        .finally(() => this.requestInProgress = false)
    }
  }

  created () {
    this.token = this.$route.params.token

    SessionManager.Instance.setTrainer(this.$route.path.includes('admin'))
  }

  setPassword (value) {
    this.password = value
    this.highlightRequirements = highlightFulfilledRequirements(this.password, this.confirmPassword)
  }

  setConfirmPassword (value) {
    this.confirmPassword = value
    this.highlightRequirements = highlightFulfilledRequirements(this.password, this.confirmPassword)
  }

  validate () {
    this.newPassError = !validatePassword(this.password)
    this.confirmError = this.password !== this.confirmPassword
    this.error = false
  }

  render (h: CreateElement) {
    return <div class='reset-password-page'>

      <Dialog>
        <Spacer />
        {this.success ?
          <div>
            <Icon name='brand/high-five' class='success-icon' />
            <Spacer />
            <Body1 align='center'>{this.$t('PAGE.RESET_PASSWORD.SUCCESS.RESET')}</Body1>
            <Spacer />
            <div class='btns-row'>
              <Button class='cancel-button' label={this.$t('PAGE.RESET_PASSWORD.BACK_TO_LOGIN')} click={() => this.$router.push('/')} type='elevated' ref='login-link' />
            </div>
          </div>

          : <div>
            <Subtitle1 align='center' class='title'>{this.$t('PAGE.RESET_PASSWORD.TITLE.RESET')}</Subtitle1>
            <Spacer />
            <Subtitle1 align='center'>{this.$t('PAGE.RESET_PASSWORD.INSTRUCTIONS.RESET')}</Subtitle1>
            <Spacer />
            <Form>
              <Input type='password' ref='input-password' label={this.$t('PASSWORD')} autofocus={true} valueChanged={this.setPassword} value={this.password}
                     class={this.error || this.newPassError ? 'error' : ''} />
              <Input type='password' ref='confirm-password' label={this.$t('PAGE.MEMBERS.CHANGE_PASSWORD.CONFIRM')} valueChanged={this.setConfirmPassword} value={this.confirmPassword}
                     class={this.confirmError ? 'error' : ''} />

              {this.error ? <InputFootnote class='error'>{this.$t('PAGE.RESET_PASSWORD.ERROR.RESET')}<Spacer /></InputFootnote> : null}

              <PasswordRequirements highlightRequirements={this.highlightRequirements} />

              {this.requestInProgress ?
                <Loader color='white' />

                : <div class='btns-row'>
                  <Button class='cancel-button' label={this.$t('CANCEL')} click={() => this.$router.push('/')} type='outline' />
                  <Button class='submit-button' label={this.$t('PAGE.RESET_PASSWORD.SUBMIT_BUTTON')} disabled={!(this.password && this.confirmPassword)} click={this.performReset}
                          type='elevated' />
                </div>}
            </Form>
          </div>}

        <LanguageSwitch />
      </Dialog>
    </div>
  }
}
